import * as React from 'react';
import {
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  useMediaQuery,
  Button,
  FormHelperText,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  fetchRoleSpecificInformation,
  createOrUpdateRole,
} from 'actions/RoleManagement/services';
import GenericSpinner from 'components/GenericSpinner/GenericSpinner';
import _ from 'lodash';
import { useSnackbar } from 'notistack';

// Import Zustand store
import { useAppStore } from '../../stores';

interface PermissionType {
  permissionId: number;
  permissionLabel: string;
  permissionExists: boolean;
}

interface SelectedUserType {
  permissionLabel: string;
}

interface ICreateOrUpdateRoleFormProps {
  formatDataAndSetInReduxStore?: (roles: unknown) => void;
}

const CreateOrUpdateRoleForm: React.FC<ICreateOrUpdateRoleFormProps> = (props) => {
  const { enqueueSnackbar } = useSnackbar();

  // Zustand store
  const rolesAndPermissions = useAppStore((state) => state.rolesAndPermissions) as {
    selectedRole: { id: number; name: string };
    userTypePermissions: PermissionType[];
    moduleTypePermissions: PermissionType[];
    selectedModuleTypes: PermissionType[];
    loadedUserType: SelectedUserType;
    selectedUserType: SelectedUserType;
  };
  const setUserTypePermissions = useAppStore((state) => state.setUserTypePermissions);
  const setModuleTypePermissions = useAppStore((state) => state.setModuleTypePermissions);
  const setSelectedUserType = useAppStore((state) => state.setSelectedUserType);
  const setSelectedModuleTypes = useAppStore((state) => state.setSelectedModuleTypes);
  const resetDataBoundPermissions = useAppStore((state) => state.resetDataBoundPermissions);
  const setLoadedUserType = useAppStore((state) => state.setLoadedUserType);

  const isRoleBeingUpdated = rolesAndPermissions.selectedRole.id === 0;

  const displayMobile = useMediaQuery('(max-width:959px)');

  const [isRequestPending, setIsRequestPending] = React.useState(false);

  const [roleName, setRoleName] = React.useState('');

  React.useEffect(() => {
    setIsRequestPending(true);
    fetchRoleSpecificInformation(rolesAndPermissions.selectedRole.id)
      .then((response: { data?: { roleName: string; roleSpecificData: { userType: PermissionType[]; moduleType: PermissionType[] } } }) => {
        if (response && response.data) {
          const { data } = response;
          const { userType, moduleType } = data.roleSpecificData;

          setRoleName(data.roleName);

          // one is set in the store to use in other parts of the app
          setUserTypePermissions(userType);
          setModuleTypePermissions(moduleType);

          // if we are adding a role, set defaults
          if (isRoleBeingUpdated) {
            // dispatch();
          } else {
            const foundSelectedUsertype = _.find(
              userType,
              (usertype) => usertype.permissionExists === true
            );

            if (foundSelectedUsertype) {
              setLoadedUserType(foundSelectedUsertype);
              setSelectedUserType(foundSelectedUsertype);
            }
          }
        }
        setIsRequestPending(false);
      })
      .catch((error: unknown) => {
        console.log(error);
        setIsRequestPending(false);
      });
    return () => {
      resetDataBoundPermissions();
    };
  }, [rolesAndPermissions.selectedRole]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>, permissionId: number = 0) => {
    const { value, checked, type } = e.target;

    // module type
    if (type === 'checkbox') {
      const { moduleTypePermissions } = rolesAndPermissions;

      // clone the array
      const modulesTypeArray = [...moduleTypePermissions];

      // find corresponding index of the permission we are changing
      const index = _.findIndex(
        modulesTypeArray,
        (module: PermissionType) => module.permissionId == permissionId
      );

      // change the permission
      modulesTypeArray[index].permissionExists = checked;

      // set the new object in store
      setSelectedModuleTypes(modulesTypeArray);
    }

    if (type === 'radio') {
      const { userTypePermissions } = rolesAndPermissions;

      const userTypesArray = [...userTypePermissions];

      const foundIndex = _.findIndex(
        userTypePermissions,
        (module: PermissionType) => module.permissionLabel === value
      );

      const foundUsersType = userTypesArray[foundIndex];

      if (foundUsersType != null) {
        setSelectedUserType(foundUsersType);
      }
    }
    if (type === 'text') {
      setRoleName(value);
    }
    return;
  };

  const [updatingRoleSpecificInfo, setUpdatingRoleSpecificInfo] = React.useState(false);

  const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setUpdatingRoleSpecificInfo(true);
    const requestFormattedRolePermissionsInfo = {
      RoleId: rolesAndPermissions.selectedRole.id,
      RoleName: roleName,
      UpdatedModulePermissions: rolesAndPermissions.selectedModuleTypes,
      OriginalUserTypePermission: rolesAndPermissions.loadedUserType,
      UpdatedUserTypePermission: rolesAndPermissions.selectedUserType,
    };
    createOrUpdateRole(requestFormattedRolePermissionsInfo)
      .then((response: { data?: { allRoles: unknown } }) => {
        if (response && response.data) {
          props.formatDataAndSetInReduxStore?.(response.data.allRoles);
          setUpdatingRoleSpecificInfo(false);
          enqueueSnackbar(
            `Role ${isRoleBeingUpdated ? 'created' : 'updated'} successfully!`,
            { variant: 'success' }
          );
        }
      })
      .catch((error: unknown) => {
        enqueueSnackbar('The following error occured: ' + error, {
          variant: 'error',
        });
        setUpdatingRoleSpecificInfo(false);
      });
  };

  const renderCorrectLoadingText = () => {
    // creation
    if (isRoleBeingUpdated) {
      if (!updatingRoleSpecificInfo) {
        return 'Loading Role Creation Form...';
      } else {
        return 'Creating Role...';
      }
    } else if (rolesAndPermissions.selectedRole.id !== 0) {
      // updating
      return `${updatingRoleSpecificInfo ? 'Updating' : 'Loading'} Role...`;
    }

    return null;
  };

  const moduleTypeError = isRoleBeingUpdated
    ? _.filter(rolesAndPermissions.selectedModuleTypes, (moduleType) => {
        return moduleType.permissionExists === true;
      }).length < 1
    : _.filter(rolesAndPermissions.moduleTypePermissions, (moduleType) => {
        return moduleType.permissionExists === true;
      }).length < 1;

  const userTypeError = _.isEmpty(rolesAndPermissions.selectedUserType);

  return (
    <Grid container>
      {isRequestPending || updatingRoleSpecificInfo ? (
        <Grid container alignItems="center" justifyContent="center">
          <Grid size={{ xs: 12, md: 12 }}>
            <GenericSpinner includeOverlay={false} padding={0} />
          </Grid>
          <Grid size={{ xs: 12, md: 12 }} className="pt-3 text-center">
            <span>{renderCorrectLoadingText()}</span>
          </Grid>
        </Grid>
      ) : (
        <Grid container>
          <form onSubmit={onFormSubmit} method={'post'} className="w-100">
            <Grid size={{ xs: 12, md: 5 }}>
              <TextField
                fullWidth
                label="Role Name"
                type="text"
                autoComplete="off"
                value={roleName}
                name="roleName"
                required
                margin="normal"
                variant="outlined"
                onChange={handleOnChange}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className="pt-2">
              <Typography variant="subtitle2">
                This name is how it will appear as the role.
              </Typography>
            </Grid>

            <Grid container className="pt-4">
              <FormControl
                component="fieldset"
                error={!!moduleTypeError}
                required
                fullWidth
              >
                <Grid size={{ xs: 12, md: 12 }}>
                  <FormLabel component="legend">Module Type</FormLabel>
                </Grid>
                {!!moduleTypeError && (
                  <Grid size={{ xs: 12, md: 12 }}>
                    <FormHelperText>
                      At least one Moduletype must be selected.
                    </FormHelperText>
                  </Grid>
                )}
                <Grid container>
                  {rolesAndPermissions.moduleTypePermissions.map(
                    (role: PermissionType, index: number) => {
                      return (
                        <Grid key={index} size={{ xs: 6, md: 4 }}>
                          <FormControlLabel
                            aria-label="modulePermissions"
                            name={'modulePermissions'}
                            control={
                              <Checkbox
                                color="primary"
                                name={`checkbox-moduletype-${index}`}
                                checked={role.permissionExists}
                                onChange={(e) =>
                                  handleOnChange(e, role.permissionId)
                                }
                              />
                            }
                            label={role.permissionLabel}
                            labelPlacement="end"
                          />
                        </Grid>
                      );
                    }
                  )}
                </Grid>
              </FormControl>

              <Grid container className="pt-3">
                <FormControl
                  component="fieldset"
                  error={userTypeError}
                  required
                  fullWidth
                  disabled={
                    rolesAndPermissions.loadedUserType.permissionLabel === 'Admin' &&
                    !isRoleBeingUpdated
                  }
                >
                  <Grid size={{ xs: 12, md: 12 }}>
                    <FormLabel component="legend">User Type</FormLabel>
                  </Grid>
                  {userTypeError && (
                    <Grid size={{ xs: 12, md: 12 }}>
                      <FormHelperText>
                        Exactly one Usertype must be selected.
                      </FormHelperText>
                    </Grid>
                  )}
                  <Grid container>
                    <RadioGroup
                      aria-label="userPermissions"
                      name={'userPermissions'}
                      value={rolesAndPermissions.selectedUserType.permissionLabel}
                      row={true}
                      onChange={(e) => handleOnChange(e)}
                    >
                      {rolesAndPermissions.userTypePermissions.map(
                        (role: PermissionType, index: number) => {
                          return (
                            <FormControlLabel
                              key={index}
                              value={role.permissionLabel}
                              control={<Radio />}
                              label={role.permissionLabel}
                            />
                          );
                        }
                      )}
                    </RadioGroup>
                  </Grid>
                </FormControl>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, md: 12 }} className={'pt-3'}>
              <Button
                className={'btn'}
                fullWidth={displayMobile ? true : false}
                variant="contained"
                type="submit"
                size="large"
                disabled={!!moduleTypeError || userTypeError}
              >
                {`${isRoleBeingUpdated ? 'Create' : 'Update'} Role`}
              </Button>
            </Grid>
          </form>
        </Grid>
      )}
    </Grid>
  );
};

export default CreateOrUpdateRoleForm;
